/**
 * Create Widget Iframe
 * Creates and manages the iframe containing the chat widget
 */

import type { LoaderConfig } from './types';

// Position styles for the iframe container (overlaps trigger position)
const POSITION_STYLES: Record<string, string> = {
  'bottom-right': 'bottom: 20px; right: 20px;',
  'bottom-left': 'bottom: 20px; left: 20px;',
  'top-right': 'top: 20px; right: 20px;',
  'top-left': 'top: 20px; left: 20px;',
};

// Resize handle position based on widget anchor (opposite corner)
const RESIZE_HANDLE_POSITIONS: Record<string, { corner: string; cursor: string; transform: string }> = {
  'bottom-right': { corner: 'top: 4px; left: 4px;', cursor: 'nw-resize', transform: 'rotate(180deg)' },
  'bottom-left': { corner: 'top: 4px; right: 4px;', cursor: 'ne-resize', transform: 'rotate(-90deg)' },
  'top-right': { corner: 'bottom: 4px; left: 4px;', cursor: 'sw-resize', transform: 'rotate(90deg)' },
  'top-left': { corner: 'bottom: 4px; right: 4px;', cursor: 'se-resize', transform: 'rotate(0deg)' },
};

export interface IframeAPI {
  element: HTMLIFrameElement;
  container: HTMLDivElement;
  /**
   * Opaque backdrop shown behind the iframe in mobile fullscreen mode to mask
   * the host page (which otherwise bleeds through the keyboard's transparency
   * on iOS Chrome / Safari). `null` in inline embed mode.
   */
  backdrop: HTMLDivElement | null;
  show: () => void;
  hide: (onComplete?: () => void) => void;
  isVisible: () => boolean;
  postMessage: (message: unknown) => void;
  updatePosition: (position: string) => void;
  destroy: () => void;
}

const DEFAULT_HANDLE_CONFIG = { corner: 'top: 4px; left: 4px;', cursor: 'nw-resize', transform: 'rotate(180deg)' };

export function createIframe(widgetUrl: string, config: LoaderConfig, onLoad: () => void): IframeAPI {
  const position = config.triggerPosition || 'bottom-right';
  const handleConfig = RESIZE_HANDLE_POSITIONS[position] ?? DEFAULT_HANDLE_CONFIG;
  const isInlineMode = config.embedMode === 'inline';

  // Create container div for positioning and animation
  const container = document.createElement('div');
  container.className = isInlineMode ? 'savanto-widget-inline' : 'savanto-widget-container';
  container.setAttribute('data-sv-component', 'widget-container');
  container.setAttribute('data-sv-mode', isInlineMode ? 'inline' : 'floating');

  if (isInlineMode) {
    // Inline mode: fill parent container, always visible.
    // Inherit min-height from the host wrapper and use flex so the iframe fills
    // the container regardless of whether the wrapper is sized via `height` or
    // `min-height`. Without this, `height: 100%` on the iframe collapses when
    // the wrapper only specifies `min-height`.
    // A soft default border keeps the embed visually grounded on the host page
    // (the floating mode relies on box-shadow for this; inline embeds have no
    // shadow, so a border is what gives them frame). Themeable via the
    // --sv-chat-inline-border CSS variable; set it to `none` on :root to remove.
    container.style.cssText = `
      position: relative;
      width: 100%;
      height: 100%;
      min-height: inherit;
      display: flex;
      flex-direction: column;
      border: var(--sv-chat-inline-border, 1px solid rgba(0, 0, 0, 0.08));
      border-radius: 16px;
      overflow: hidden;
    `;
  } else {
    // Floating mode: fixed position with animation
    container.style.cssText = `
      position: fixed;
      ${POSITION_STYLES[position] || POSITION_STYLES['bottom-right']}
      z-index: 999999;
      width: 420px;
      height: 600px;
      max-width: calc(100vw - 40px);
      max-height: calc(100vh - 120px);
      min-width: 320px;
      min-height: 400px;
      border-radius: 16px;
      overflow: hidden;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
      opacity: 0;
      transform: scale(0.95) translateY(10px);
      transition: opacity 0.2s ease, transform 0.2s ease;
      pointer-events: none;
      display: none;
    `;
  }

  // Create resize handle
  const resizeHandle = document.createElement('div');
  resizeHandle.className = 'savanto-resize-handle';
  resizeHandle.setAttribute('data-sv-component', 'resize-handle');
  resizeHandle.style.cssText = `
    position: absolute;
    ${handleConfig.corner}
    width: 16px;
    height: 16px;
    cursor: ${handleConfig.cursor};
    z-index: 10;
    opacity: 0;
    transition: opacity 0.2s ease;
    background-color: var(--sv-text-secondary, #64748b);
    -webkit-mask: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M21 8L8 21M21 15L15 21' stroke='%23000' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat center;
    mask: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M21 8L8 21M21 15L15 21' stroke='%23000' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat center;
    -webkit-mask-size: 14px 14px;
    mask-size: 14px 14px;
    transform: ${handleConfig.transform};
  `;

  // Create iframe
  // Note: Created before resize handlers so they can reference it
  const iframe = document.createElement('iframe');
  iframe.className = 'savanto-widget-iframe';
  iframe.setAttribute('title', 'Savanto Chat Widget');
  iframe.setAttribute('allow', 'clipboard-write; microphone');
  iframe.setAttribute('data-sv-component', 'widget-iframe');

  // Security: sandbox with necessary permissions
  iframe.setAttribute(
    'sandbox',
    'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation',
  );

  iframe.style.cssText = `
    flex: 1 1 auto;
    width: 100%;
    height: 100%;
    min-height: inherit;
    border: none;
    background: transparent;
    display: block;
  `;

  // Load event
  iframe.onload = () => {
    onLoad();
  };

  // Set source
  iframe.src = widgetUrl;

  // Resize logic
  let isResizing = false;
  let startX = 0;
  let startY = 0;
  let startWidth = 0;
  let startHeight = 0;

  const onMouseDown = (e: MouseEvent) => {
    e.preventDefault();
    isResizing = true;
    startX = e.clientX;
    startY = e.clientY;
    startWidth = container.offsetWidth;
    startHeight = container.offsetHeight;
    resizeHandle.style.opacity = '1';
    // Disable pointer events on iframe so mouse events pass through during resize
    iframe.style.pointerEvents = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const onMouseMove = (e: MouseEvent) => {
    if (!isResizing) return;
    const deltaX = e.clientX - startX;
    const deltaY = e.clientY - startY;

    // Calculate new size based on widget position
    let newWidth = startWidth;
    let newHeight = startHeight;

    if (position === 'bottom-right') {
      newWidth = startWidth - deltaX;
      newHeight = startHeight - deltaY;
    } else if (position === 'bottom-left') {
      newWidth = startWidth + deltaX;
      newHeight = startHeight - deltaY;
    } else if (position === 'top-right') {
      newWidth = startWidth - deltaX;
      newHeight = startHeight + deltaY;
    } else if (position === 'top-left') {
      newWidth = startWidth + deltaX;
      newHeight = startHeight + deltaY;
    }

    // Apply constraints
    newWidth = Math.max(320, Math.min(newWidth, window.innerWidth - 40));
    newHeight = Math.max(400, Math.min(newHeight, window.innerHeight - 40));

    container.style.width = `${newWidth}px`;
    container.style.height = `${newHeight}px`;
  };

  const onMouseUp = () => {
    isResizing = false;
    resizeHandle.style.opacity = '0.6';
    // Re-enable pointer events on iframe after resize completes
    iframe.style.pointerEvents = '';
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  };

  // Show handle on container hover
  container.addEventListener('mouseenter', () => {
    resizeHandle.style.opacity = '0.6';
  });
  container.addEventListener('mouseleave', () => {
    if (!isResizing) resizeHandle.style.opacity = '0';
  });

  resizeHandle.addEventListener('mousedown', onMouseDown);

  // Append elements to container
  container.appendChild(iframe);

  // Only add resize handle in floating mode
  if (!isInlineMode) {
    container.appendChild(resizeHandle);
  }

  // Backdrop for mobile fullscreen mode. Mobile WebViews render the host page
  // through the on-screen keyboard's transparency, which lets the underlying
  // site bleed through behind our iframe. The backdrop is a separate
  // position:fixed div sitting just under the iframe (z-index 999998 vs
  // 999999) that covers the full layout viewport — even the area behind the
  // keyboard — with the widget's own background color, so visitors see the
  // chat widget aesthetic instead of the host page.
  // Only created for floating mode; inline embeds are sized by the host
  // container and don't need it.
  let backdrop: HTMLDivElement | null = null;
  if (!isInlineMode) {
    const isDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
    const defaultBg = isDark ? '#1f1f1f' : '#ffffff';
    const bgColor = config.theme?.colors?.widgetBackground || defaultBg;
    backdrop = document.createElement('div');
    backdrop.className = 'savanto-widget-backdrop';
    backdrop.setAttribute('data-sv-component', 'widget-backdrop');
    backdrop.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 999998;
      background-color: ${bgColor};
      display: none;
      pointer-events: none;
    `;
    document.body.appendChild(backdrop);
  }

  // Append container to target (inline mode uses containerSelector, floating mode uses body)
  if (isInlineMode && config.containerSelector) {
    const targetContainer = document.querySelector(config.containerSelector);
    if (targetContainer) {
      targetContainer.appendChild(container);
    } else {
      console.warn(`[Savanto] Container not found: ${config.containerSelector}. Falling back to body.`);
      document.body.appendChild(container);
    }
  } else {
    document.body.appendChild(container);
  }

  // Track visibility state
  let isVisible = isInlineMode; // Inline mode is always visible

  return {
    element: iframe,
    container,
    backdrop,
    show: () => {
      if (isVisible) return;
      isVisible = true;

      if (isInlineMode) {
        // Inline mode: always visible, no animation needed
        return;
      }

      // Floating mode: animate in
      container.style.display = 'block';
      // Trigger reflow for animation
      container.offsetHeight;
      container.style.opacity = '1';
      container.style.transform = 'scale(1) translateY(0)';
      container.style.pointerEvents = 'auto';
    },
    hide: (onComplete?: () => void) => {
      if (!isVisible) return;

      if (isInlineMode) {
        // Inline mode: don't hide, just call callback
        onComplete?.();
        return;
      }

      // Floating mode: animate out
      isVisible = false;
      container.style.opacity = '0';
      container.style.transform = 'scale(0.95) translateY(10px)';
      container.style.pointerEvents = 'none';
      // Hide after animation completes
      setTimeout(() => {
        if (!isVisible) {
          container.style.display = 'none';
        }
        onComplete?.();
      }, 200);
    },
    isVisible: () => isVisible,
    postMessage: (message: unknown) => {
      if (iframe.contentWindow) {
        iframe.contentWindow.postMessage(message, '*');
      }
    },
    updatePosition: (newPosition: string) => {
      // Remove old position styles
      container.style.top = '';
      container.style.bottom = '';
      container.style.left = '';
      container.style.right = '';
      // Apply new position
      const posStyle = POSITION_STYLES[newPosition] ?? 'bottom: 20px; right: 20px;';
      const styles = posStyle.split(';').filter((s) => s.trim());
      for (const style of styles) {
        const [prop, value] = style.split(':').map((s) => s.trim());
        if (prop && value) {
          container.style.setProperty(prop, value);
        }
      }
    },
    destroy: () => {
      exitHostTakeover();
      container.remove();
      backdrop?.remove();
    },
  };
}

/**
 * Switch the widget between desktop (floating) and mobile (fullscreen) modes.
 *
 * In mobile fullscreen mode we do three things to guarantee the host page
 * doesn't leak through behind our iframe (mobile WebViews are inconsistent
 * with how they handle `position: fixed` when the on-screen keyboard opens —
 * the iframe and any fixed-position backdrop can end up clipped or scrolled,
 * exposing host page content in the gap below the iframe and through the
 * keyboard's transparency):
 *
 *   1. Pin the iframe container to the layout viewport
 *   2. Show the backdrop (covers most fixed-clipping scenarios)
 *   3. Hide every other direct child of `<body>` and paint `<body>` itself
 *      with the widget background color, so even if iOS Chrome breaks our
 *      fixed positioning, there's no host content left to bleed through.
 *
 * The takeover state is fully restored on close, resize-to-desktop, or
 * destroy.
 */

/** State captured on takeover, restored when mobile fullscreen exits. */
interface HostTakeoverState {
  body: { background: string; overflow: string };
  html: { background: string; overflow: string };
  childrenVisibility: { el: HTMLElement; visibility: string }[];
}

let takeover: HostTakeoverState | null = null;

export function applyMobileStyles(api: IframeAPI, isMobile: boolean): void {
  // Hide / restore the desktop drag-to-resize handle. On touch devices the
  // container's `mouseenter` listener fires on first tap and reveals the
  // handle, which is meaningless in mobile fullscreen (nothing to resize) and
  // looks like a visual glitch.
  const resizeHandle = api.container.querySelector<HTMLElement>('[data-sv-component="resize-handle"]');
  if (resizeHandle) resizeHandle.style.display = isMobile ? 'none' : '';

  if (isMobile) {
    api.container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      max-width: none;
      max-height: none;
      border-radius: 0;
      z-index: 999999;
    `;
    if (api.backdrop) api.backdrop.style.display = 'block';
    enterHostTakeover(api);
  } else {
    exitHostTakeover();
    if (api.backdrop) api.backdrop.style.display = 'none';
  }
}

function enterHostTakeover(api: IframeAPI): void {
  if (takeover) return;

  const body = document.body;
  const html = document.documentElement;
  const bgColor = api.backdrop?.style.backgroundColor || '#ffffff';

  const childrenVisibility: { el: HTMLElement; visibility: string }[] = [];
  for (const child of Array.from(body.children)) {
    if (child === api.container || child === api.backdrop) continue;
    const el = child as HTMLElement;
    childrenVisibility.push({ el, visibility: el.style.visibility });
    el.style.visibility = 'hidden';
  }

  takeover = {
    body: { background: body.style.background, overflow: body.style.overflow },
    html: { background: html.style.background, overflow: html.style.overflow },
    childrenVisibility,
  };

  body.style.background = bgColor;
  body.style.overflow = 'hidden';
  html.style.background = bgColor;
  html.style.overflow = 'hidden';
}

function exitHostTakeover(): void {
  if (!takeover) return;

  document.body.style.background = takeover.body.background;
  document.body.style.overflow = takeover.body.overflow;
  document.documentElement.style.background = takeover.html.background;
  document.documentElement.style.overflow = takeover.html.overflow;
  for (const { el, visibility } of takeover.childrenVisibility) {
    el.style.visibility = visibility;
  }

  takeover = null;
}
